export interface AhaModal extends HTMLAhaModalElement {}

/**
 * ==========================================================================
 * ============================= DEVELOPER NOTE =============================
 * ==========================================================================
 *
 * Most of the functionality of `<aha-modal>` actually comes from the `<aha-dialog>` component.
 * Unfortunately Stencil doesn't provide any way to sub-class elements or really any decent
 * way to extract things like props/state between components, so we're left with only one
 * alternative ... namely make props/events in our "sub-class" elements that reference the original props/events in the parent `<aha-dialog>` component.
 *
 * By doing this, we ensure that any changes to `<aha-dialog>` will also happen in any component
 * that relies on native <dialog> functionality (namely lifting to the top rendering context).
 *
 */

import {
  Component,
  Host,
  h,
  Element,
  Prop,
  Method,
  Event,
  EventEmitter,
  Listen,
  Watch,
  State,
} from '@stencil/core';
import classcat from 'classcat';
import { isNode } from '../../utils/domHelpers';

import { Directions, Positions } from '../aha-dialog/types';
import { Sizes } from './types';
import * as Common from '../aha-dialog/dialog-common';

/**
 * A component which makes creating modal style elements easy. This component is a wrapper around the native `<dialog>` element and is intended to be used in place of the `<dialog>` element. Most of the functionality comes from functionality shared with the `<aha-dialog>` element. Additions beyond the `<aha-dialog>` component are:
 *
 * * `<aha-modal>` elements can have specific, predefined sizes.
 * * `<aha-modal>` elements are meant to be used in conjunction with `<aha-modal-header>`, `<aha-modal-body>`, and `<aha-modal-footer>` elements to rapidly and easily build fully styled modals.
 * * `<aha-modal>` elements can be dragged around the screen by clicking and dragging on a nested `<aha-modal-header>` component. This ability can be toggled off by setting the `prevent-drag` attribute on `<aha-modal-header>`.
 *
 * ## Public methods
 *
 * `toggle()`
 * -- Use this method to toggle the open/closed state of the dialog. If the dialog is open, it will close it. If the dialog is closed, it will open it.
 *
 * `show()`
 * -- Use this method to open the dialog. If the dialog is already open, this method will do nothing.
 *
 * `hide()`
 * -- Use this method to close the dialog. If the dialog is already closed, this method will do nothing.
 *
 * `bringToFront()`
 * -- Use this method to bring the dialog to the front of the stacking context. This is useful if you have multiple dialogs open, and you want to make sure that the dialog you are working with is on top of all the others.
 *
 * `reposition()`
 *  -- Use if the `<aha-modal>` is positioned relative to some other element and you need to reposition the `<aha-modal>` to be in the correct place on the screen, relative to that element. You likely will never need to call this method, however if you do, it is there for your use.
 *
 * @slot modal-header - Assigns contents to the "header" part of the modal. If you utilize the `<aha-modal-header>` component, contents will automatically be slotted here.
 * @slot modal-body - Assigns contents to the "body" part of the modal. If you utilize the `<aha-modal-body>` component, contents will automatically be slotted here.
 * @slot default - Contents of the default slot. This will appear in between the modal-header and modal-footer slots and will not have any default padding (by default the `<aha-modal-body>` component will have padding).
 * @slot modal-footer - Assigns contents to the "header" part of the modal. If you utilize the `<aha-modal-footer>` component, contents will automatically be slotted here.
 */
@Component({
  tag: 'aha-modal',
  styleUrl: 'aha-modal.css',
  shadow: true,
})
export class AhaModal {
  // ================================================================================================ //
  // ========== Duplicated content from AHA-DIALOG because Stencil cannot extend classes. =========== //
  // ============ Have only updated the docs and event names to be aha-modal specific.  ============= //
  // ================================================================================================ //

  // Reference to the native <dialog> element.
  protected dialogElem!: HTMLDialogElement;

  // Reference to the inner "contents" area of the native <dialog> element.
  protected innerElem?: HTMLElement;

  // The element which a relatively positioned `<aha-modal>` will position itself around. Note that this is only set at specific instances in the component's lifecycle, typically when first attached to a page, and any time the `position` property is changed from some value to `relative`.
  public positionTarget?: HTMLElement;

  // Scroll event handler function.
  protected scrollEvent?: any;

  // The styles that exist on the <aha-modal> immediately before the dialog is opened. This gives us a good "reset" style to return to when the <aha-modal> is closed.
  protected stylesBeforeOpened: string = '';

  // Various states to control open/close animations.
  @State() closing?: boolean;
  @State() opening?: boolean;

  /** Whether or not an `<aha-modal>` element has been cancelled by pressing the escape key. **(Read only)** */
  @Prop() cancelled?: boolean;

  /** When `persistent` is true, two things happen:
   *
   * 1. the `<aha-modal>` component cannot be dismissed by pressing the "Escape" key
   * 2. if the `hideBackdrop` option is set to true, the `<aha-modal>` component will not be closed automatically when clicked outside of the visible area
   *
   * Effectively this means that if you want to close the `<aha-modal>` component, you'll need to handle the logic for doing so on your own.
   */
  @Prop({ reflect: true }) persistent?: boolean;

  /** When `hideWhenOutside` is true and if `hideBackdrop` is true, then this `<aha-modal>` will be hidden when the user moves their pointer outside the "inner" dialog's contents or `positionTarget`.
   */
  @Prop({ reflect: true, attribute: 'hide-when-outside' }) hideWhenOutside?: boolean;

  /** The amount a relatively positioned `<aha-modal>` element should be offset from its triggering element, relative to its opened direction. If the `position` attribute is not set to "relative", this setting will have no effect. Accepts a value of:
   *
   * * `undefined` (which will place the `<aha-modal>` just outside the triggering element)
   * * the string `inset` (which will place the `<aha-modal>` "inside" the boundaries of the triggering element)
   * * any numerical value, eg, `5` or `10` or `20` (which will place the `<aha-modal>` outside the triggering element by that `n` amount of pixels)
   */
  @Prop({ reflect: true }) offset?: string | number;

  /** Whether or not the `<aha-modal>` element is open on the page. */
  @Prop({ reflect: true }) open?: boolean;
  @Watch('open')
  protected handleOpen() {
    Common.handleOpen.call(this);
  }

  /** Whether or not the `<aha-modal>` element has a visible backdrop when opened. When hidden, the backdrop will never show.
   *
   * By default the top-most open `<aha-modal>` always has a backdrop, unless the attribute `hide-backdrop` is present.
   *
   * The backdrop can be styled directly by targeting the `aha-modal::part(dialog)::backdrop` CSS selector. */
  @Prop({ reflect: true, attribute: 'hide-backdrop' }) hideBackdrop?: boolean;
  @Watch('hideBackdrop')
  private updateBackdrops() {
    Common.updateBackdrops.call(this);
  }

  /** The positioning strategy of the opened dialog.
   *
   * If the value is set to "relative", see the documentation for the `rel` attribute for further details.
   */
  @Prop({ reflect: true }) position: Positions = 'h-center';
  @Watch('position')
  private handlePositionChange() {
    Common.handlePositionChange.call(this);
  }

  /** The element that the opened dialog will be positioned relative to. The value can be one of four values:
   *
   * * `undefined`
   * * an HTML element (set via JS)
   * * a string which represents a CSS selector
   * * a string representing a pair of `x,y` coordinates
   *
   * ##### When: The relative element is `undefined`
   * The relative element is `undefined`, and so the element which the `<aha-modal>` is set relative to is the immediately preceeding sibling element.
   *
   * ```<aha-modal position="relative"></aha-modal>```
   *
   * ##### When: The relative element is set to an HTML element
   * The relative element is set to a real DOM node via javascript.
   *
   * ```
   * dialogElem.rel = document.querySelector('button');
   * ```
   *
   * ##### When: The relative element is set via CSS selector
   * The relative element will be the first element on the page that matches the CSS selector.
   *
   * ```
   * <aha-modal position="relative" rel="#foo"></aha-modal>
   * ```
   * The code above will find the element on the page with the id of "foo" and position the dialog relative to that.
   *
   * It is also possible to use a slightly expanded CSS syntax to select the closest parent element that matches a CSS selector by using our custom `::closest()` pseudo-element.
   * This selector will find the _closest parent element_ matching the `::closest()` pseudo element and then match any remaining CSS selector based on that closest element.
   *
   * ```
   * <aha-modal position="relative" rel="::closest(.bar) .baz"></aha-modal>
   * ```
   *
   * This selector will find the closest parent element with the class of "bar" then position the dialog relative to the position of the ".baz" element inside that.
   *
   * ##### When: The `rel` value is a set to a string of `x,y` coordinates
   * The `<aha-modal>` will be positioned relative to this point on the page.
   *
   * ```
   * <aha-modal position="relative" rel="300,400"></aha-modal>
   * ```
   *
   */
  @Prop({ reflect: true }) rel: undefined | HTMLElement | string;
  @Watch('rel')
  private willReposition() {
    this.reposition();
  }

  /** The direction(s) in which a relatively positioned dialog can be opened. Can accept either a single value (ie, `bottom-start`) or a comma separated list of values (ie, `bottom-start,right,top-start`). If the `position` attribute is not set to "relative", this setting will have no effect. */
  @Prop({ reflect: true }) directions?: Directions;
  @Watch('directions')
  private sanitizeDirections(value: string, oldValue: string, attr: string) {
    Common.sanitizeDirections.call(this, value, oldValue, attr);
  }

  /** The direction in which the dialog is current opened. Only applicable when used with `relative` positioning strategy. */
  @State() openedDirection?: string;

  /** This determines whether or the area outside the `<aha-modal>` can be scrolled while the dialog is open. */
  @Prop({ reflect: true }) restrict?: boolean;
  @Watch('restrict')
  private handleRestrict() {
    Common.handleRestrict.call(this);
  }

  /** Whether or not the `<aha-modal>` is currently in a "loading" state. When set to true, a spinner will appear and any contents of the dialog cannot be interacted with until the loading attribute is set to false. */
  @Prop({ reflect: true }) loading?: boolean;

  /** Whether or not this particual dialog is the top-most dialog of all the <aha-modal> elements. **(Read only)** */
  @Prop() maximal: boolean = false;

  /**
   * Triggers immediately before the `<aha-modal>` opens.
   */
  @Event({
    eventName: 'aha-modal:open',
    cancelable: true,
    composed: true,
    bubbles: true,
  }) //eslint-disable-line @stencil/decorators-style
  openEvent!: EventEmitter;

  /**
   * Triggers immediately after the `<aha-modal>` opens.
   */
  @Event({
    eventName: 'aha-modal:opened',
    cancelable: true,
    composed: true,
    bubbles: true,
  }) //eslint-disable-line @stencil/decorators-style
  openedEvent!: EventEmitter;

  /**
   * Triggers immediately before the `<aha-modal>` closes.
   */
  @Event({
    eventName: 'aha-modal:close',
    cancelable: true,
    composed: true,
    bubbles: true,
  }) //eslint-disable-line @stencil/decorators-style
  closeEvent!: EventEmitter;

  /**
   * Triggers immediately after the `<aha-modal>` closes.
   */
  @Event({
    eventName: 'aha-modal:closed',
    cancelable: true,
    composed: true,
    bubbles: true,
  }) //eslint-disable-line @stencil/decorators-style
  closedEvent!: EventEmitter;

  /**
   * Triggers when the "Escape" key is pressed and before the `<aha-modal>` is closed. Fires unless the `persistent` attribute is set.
   */
  @Event({
    eventName: 'aha-modal:cancel',
    cancelable: true,
    composed: true,
    bubbles: true,
  }) //eslint-disable-line @stencil/decorators-style
  cancelEvent!: EventEmitter;

  /** A method by which the `<aha-modal>`'s `cancel` event can manually be fired. Typically only used when working with elements which wrap an `<aha-modal>`. */
  @Method()
  async emitCancelEvent() {
    Common.emitCancelEvent.call(this);
  }

  /**
   * Triggers when the "Escape" key is pressed and after the `<aha-modal>` is closed. Fires unless the `persistent` attribute is set.
   */
  @Event({
    eventName: 'aha-modal:cancelled',
    cancelable: true,
    composed: true,
    bubbles: true,
  }) //eslint-disable-line @stencil/decorators-style
  cancelledEvent!: EventEmitter;

  /**
   * Triggers when the user mouses outside of both the "inner" contents of the `<aha-modal>` and any `positionTarget` element.
   */
  @Event({
    eventName: 'aha-modal:pointer-outside',
    cancelable: true,
    composed: true,
    bubbles: true,
  }) //eslint-disable-line @stencil/decorators-style
  pointerOutsideEvent!: EventEmitter;

  /**
   * Triggers when the user mouses inside either the "inner" contents of the `<aha-modal>` or any `positionTarget` element.
   */
  @Event({
    eventName: 'aha-modal:pointer-inside',
    cancelable: true,
    composed: true,
    bubbles: true,
  }) //eslint-disable-line @stencil/decorators-style
  pointerInsideEvent!: EventEmitter;

  /** Toggles the `open` state of the dialog. */
  @Method()
  async toggle() {
    Common.toggle.call(this);
  }

  /** Shows the dialog. */
  @Method()
  async show() {
    Common.show.call(this);
  }

  /** Hides the dialog. */
  @Method()
  async hide(immediate?: boolean) {
    Common.hide.call(this, immediate);
  }

  /** Bring the specified dialog to the top-most rendering layer, if it is not already there. Because native <dialog> elements do not have a concept of user manipulatable stacking order (z-index) the only way to force one on top is to close it and then immediately reopen it. */
  @Method()
  async bringToFront() {
    Common.bringToFront.call(this);
  }

  /** Handle canceling the dialog (closing it via "Escape"). */
  @Listen('cancel')
  private handleCancel(e: Event) {
    Common.handleCancel.call(this, e);
  }

  /** When this particular `<aha-modal>` element is closed, then reset all the styles to give us a good starting point for re-opening. */
  @Listen('aha-modal:closed')
  private clearInnerStyles(e: CustomEvent) {
    Common.clearInnerStyles.call(this, e);
  }

  /** Event that fires when opening/closing animations are complete. */
  protected handleAnimationEnd(event: AnimationEvent) {
    Common.handleAnimationEnd.call(this, event);
  }

  /** Event that fires when user clicks within the native <dialog> element. */
  protected handleDialogClick(event: MouseEvent) {
    Common.handleDialogClick.call(this, event);
  }

  /** Event that fires when mouses within the native <dialog> element. */
  protected handleDialogPointerMove(event: PointerEvent) {
    Common.handleDialogPointerMove.call(this, event);
  }

  connectedCallback() {
    Common.connectedCallback.call(this);
  }

  componentDidLoad() {
    Common.componentDidLoad.call(this);
  }

  disconnectedCallback() {
    Common.disconnectedCallback.call(this);
  }

  /** Reposition the `<aha-modal>` relative to the position of it's `rel` element. */
  @Method()
  async reposition(ignoredDirections: Set<string> = new Set()) {
    Common.reposition.call(this, ignoredDirections);
  }

  get isRelativelyPositioned() {
    return Common.isRelativelyPositioned.call(this);
  }

  protected possibleDirections: Set<string> = new Set();

  // ================================================================================================ //
  // ================== Things specific to AHA-MODAL (not shared with AHA-DIALOG). ================== //
  // ================================================================================================ //

  @Element() host!: HTMLAhaModalElement;

  /** Move the modal to the coordinates specified (measured from the top/left corner of the page). Can accept either numbers (which will be converted to a pixel value) or any other valid CSS unit (`1em`, `2rem`, `var(--my-amount)`). */
  @Method()
  async moveTo(left: number | string, top: number | string) {
    if (this.dialogElem) {
      left = typeof left === 'number' ? `${left}px` : left;
      top = typeof top === 'number' ? `${top}px` : top;
      Object.assign(this.dialogElem.style, { left, top });
    }
  }

  /** The size of the modal. Can accept a few different options.
   *
   * `auto`: Modal will resize to fit whatever size content it has. This value can further be manipulated via CSS variables.
   *
   * `small`: 300px wide
   *
   * `medium`: 800px wide
   *
   * `large`: Minimum 800px wide, maximum 90vw wide
   *
   */
  @Prop({ reflect: true }) size: Sizes = 'auto';

  /** Whether or not the modal is currently being dragged. */
  @State() dragging?: boolean;

  /** Whether or not the modal is currently capable of being dragged. */
  @State() canDrag?: boolean;

  @Watch('canDrag')
  private toggleDragging(canDrag: boolean) {
    if (canDrag) {
      this.host.addEventListener('mousemove', this.handleDragging);
    } else {
      this.host.removeEventListener('mousemove', this.handleDragging);
      this.dragStartLeft = 0;
      this.dragStartTop = 0;
    }
  }

  private dragStartLeft: number = 0;
  private dragStartTop: number = 0;

  private handleDragging(e: MouseEvent) {
    // Ensure that only the modal that was dragged is actually dragged, not any parent modals.
    const ahaModalElem = e.composedPath().find(n => isNode(n) && n.nodeName === 'AHA-MODAL');
    if (ahaModalElem === this.host) {
      const x = e.clientX - this.dragStartLeft;
      const y = e.clientY - this.dragStartTop;
      this.dragging = true;
      this.moveTo(x, y);
    }
  }

  @Listen('aha-modal-header:close')
  private closeModal(event: CustomEvent) {
    // Ensure that only the modal that was clicked closed is actually closed, not any parent modals.
    const ahaModalElem = event.composedPath().find(n => isNode(n) && n.nodeName === 'AHA-MODAL');
    if (ahaModalElem === this.host) this.hide();
  }

  private handleMouseDown(event: MouseEvent) {
    if (
      event.composedPath().find((n: any) => {
        return n.nodeType === 1 && n.tagName === 'AHA-MODAL-HEADER' && !n.preventDrag;
      })
    ) {
      if (this.dialogElem) {
        const { left, top } = this.dialogElem.getBoundingClientRect();
        this.dragStartLeft = event.clientX - left;
        this.dragStartTop = event.clientY - top;
        this.canDrag = true;
      }
    }
  }

  private handleMouseUp() {
    this.canDrag = false;
    this.dragging = false;
  }

  private afterCommonConnectedCallback() {
    this.handleMouseDown = this.handleMouseDown.bind(this);
    this.handleMouseUp = this.handleMouseUp.bind(this);
    this.handleDragging = this.handleDragging.bind(this);
  }

  private afterFadeOut() {
    if (!this.open && this.dialogElem) {
      // Reset dragged modal position.
      this.dialogElem.style.left = '';
      this.dialogElem.style.top = '';
    }
  }

  render() {
    return (
      <Host
        role="dialog"
        aria-modal="true"
        maximal={this.maximal}
        direction={this.openedDirection}
        onMouseDown={this.handleMouseDown}
        onMouseUp={this.handleMouseUp}
        dragging={this.dragging}
      >
        <dialog
          ref={el => (this.dialogElem = el as HTMLDialogElement)}
          // @ts-ignore - onCancel is not a typescript recognized event, even though it is native to the <dialog> element.
          onCancel={this.handleCancel}
          // @ts-ignore
          onClick={this.handleDialogClick}
          onPointerMove={this.handleDialogPointerMove}
          tabIndex={-1}
        >
          <div
            id="inner"
            class={classcat({ opening: this.opening, closing: this.closing })}
            part="dialog"
            ref={el => (this.innerElem = el)}
            onAnimationEnd={this.handleAnimationEnd}
          >
            <slot name="modal-header"></slot>
            <slot name="modal-body"></slot>
            <slot></slot>
            <slot name="modal-footer"></slot>

            <div id="loader">
              <aha-spinner size="6ex" opacity={0.8} part="loader"></aha-spinner>
            </div>
          </div>
        </dialog>
      </Host>
    );
  }
}
